import { type URLSearchParams } from 'url';

import {
  type FilterOperatorRange,
  type FilterOperatorValue,
  type QueryStateFilter,
  type QueryStateFilterRange,
  type SolQueryFilter,
  type SolQueryFilterOperator,
} from '../types';

export const isFilterRange = (
  e: QueryStateFilterRange | QueryStateFilter,
): e is QueryStateFilterRange =>
  (e as QueryStateFilterRange).min != null || (e as QueryStateFilterRange).max != null;

export const isFilterOperatorRange = (
  e: FilterOperatorRange | FilterOperatorValue,
): e is FilterOperatorRange =>
  (e as FilterOperatorRange)?.min != null || (e as FilterOperatorRange)?.max != null;

const OR_SEPARATOR = ',';
const AND_SEPARATOR = '+';

const filterOperatorExceptions: Record<string, SolQueryFilterOperator> = {
  webActivityPagesList: 'contains',
};

const parseFilterFromQS = (field: string, operand: string): QueryStateFilter => {
  const isNegated = field.endsWith('!');
  field = field.replace(/!$/, '');
  const isRangeValue =
    (operand.startsWith('[') || operand.startsWith('(')) &&
    (operand.endsWith(']') || operand.endsWith(')'));

  if (isRangeValue) {
    const firstChar = operand[0];
    const lastChar = operand[operand.length - 1];
    const rangeValuesStr = operand.slice(1, operand.length - 1);
    const [minOperand, maxOperand] = rangeValuesStr.split(',');

    return {
      field,
      min:
        minOperand.length === 0 ? undefined : { inclusive: firstChar === '[', operand: minOperand },
      max:
        maxOperand.length === 0 ? undefined : { inclusive: lastChar === ']', operand: maxOperand },
    } as QueryStateFilterRange;
  } else {
    const booleanOperator = operand.includes(AND_SEPARATOR) ? 'AND' : 'OR';
    const values =
      booleanOperator === 'AND' ? operand.split(AND_SEPARATOR) : operand.split(OR_SEPARATOR);

    let operator: SolQueryFilterOperator = isNegated ? 'notIn' : 'in';
    if (filterOperatorExceptions[field]) {
      operator = filterOperatorExceptions[field];
    }

    return {
      field,
      operator,
      operand: values.map((value) => decodeURIComponent(value)),
      booleanOperator,
    };
  }
};

const stripPrefix = (prefix: string, field: string) => {
  // The prefix is always at the beginning of the field.
  return field.substring(prefix.length + 1);
};

export const getFiltersFromURLSearchParams = (
  searchParams: URLSearchParams,
  prefix: string,
  initialFilters: QueryStateFilter[],
) => {
  const filterEntries = [...searchParams.entries()].filter((entry) =>
    entry[0].startsWith(`${prefix}.`),
  );

  // Get any default filters which haven't been overridden by search params.
  const remainingInitialFilters = initialFilters.filter(
    (filter) =>
      filterEntries.some(
        ([field]) =>
          filter.field === field ||
          (field.endsWith('!') && field.substring(0, field.length - 1)) === field,
      ) === false,
  );

  // Get any overriding filters from query params.
  const filtersFromQueryParams = filterEntries.map(([field, operand]) =>
    parseFilterFromQS(stripPrefix(prefix, field), operand),
  );

  return [...remainingInitialFilters, ...filtersFromQueryParams];
};

const stringifyFilterRange = (filter: QueryStateFilterRange) => {
  let minString;
  let maxString;

  if (filter.min?.operand != null) {
    minString = `${filter.min.inclusive ? '[' : '('}${filter.min.operand}`;
  } else {
    minString = '[';
  }

  if (filter.max?.operand != null) {
    maxString = `${filter.max.operand}${filter.max.inclusive ? ']' : ')'}`;
  } else {
    maxString = ']';
  }

  return `${minString},${maxString}`;
};

export const stringifyFilter = (filter: QueryStateFilter) => {
  if (isFilterRange(filter)) {
    return stringifyFilterRange(filter);
  } else {
    const { operand, booleanOperator = 'OR' } = filter;
    const newOperands = Array.isArray(operand)
      ? operand
          .map((value) => encodeURIComponent(value))
          .join(booleanOperator === 'AND' ? AND_SEPARATOR : OR_SEPARATOR)
      : encodeURIComponent(operand);
    return newOperands;
  }
};

export const addURLSearchParamsFromFilters = (
  searchParams: URLSearchParams,
  prefix: string,
  filters: QueryStateFilter[],
) => {
  filters.forEach((filter) => {
    let fieldName = filter.field;
    if (!isFilterRange(filter) && filter.operator === 'notIn') {
      fieldName += '!';
    }
    searchParams.set(`${prefix}.${fieldName}`, stringifyFilter(filter));
  });
};

export const getSolQueryFiltersFromQueryParamsFilters = (
  filters: QueryStateFilter[],
): SolQueryFilter[] => {
  return filters.reduce<SolQueryFilter[]>((memo, filter) => {
    if (isFilterRange(filter)) {
      // Convert a single FilterRange filter into 2 Sol filters
      const rangeFilters: SolQueryFilter[] = [];

      if (filter.min != null) {
        rangeFilters.push({
          field: filter.field,
          operator: filter.min.inclusive ? 'gte' : 'gt',
          operand: filter.min.operand,
        });
      }

      if (filter.max != null) {
        rangeFilters.push({
          field: filter.field,
          operator: filter.max.inclusive ? 'lte' : 'lt',
          operand: filter.max.operand,
        });
      }

      return memo.concat(rangeFilters);
    }

    return memo.concat(filter);
  }, []);
};
